import { sumTotalsForModeledVariables } from '@/dashboards/SpendSummary/helpers/sumTotalsForModeledVariables';

export interface Totals {
    totalContribution: number;
    totalSpend: number;
    totalContributionProduct: number;
}

export interface SpendRow {
    contribution: number;
    spend: number;
    contributionProduct: number;
    variable?: string;
}

export const INITIAL_TOTALS: Totals = Object.freeze({
    totalContribution: 0,
    totalSpend: 0,
    totalContributionProduct: 0,
});

export const sumTotals = (rows: SpendRow[]): Totals =>
    rows.reduce<Totals>(
        (acc, curr) => ({
            totalContribution:
                acc.totalContribution + (Number(curr.contribution) || 0),
            totalSpend: acc.totalSpend + (Number(curr.spend) || 0),
            totalContributionProduct:
                acc.totalContributionProduct +
                (Number(curr.contributionProduct) || 0),
        }),
        { ...INITIAL_TOTALS },
    );

/**
 * Computes standard totals and ROI-specific totals for a set of spend summary rows.
 */
export const calculateSpendTotals = (
    rows: SpendRow[] | undefined,
    modeledVariablesOnlyInTotalMediaRoi: boolean,
): { totals: Totals; roiTotals: Totals } => {
    if (!rows || rows.length === 0) {
        return { totals: INITIAL_TOTALS, roiTotals: INITIAL_TOTALS };
    }

    const totals = sumTotals(rows);
    if (!modeledVariablesOnlyInTotalMediaRoi) {
        return { totals, roiTotals: totals };
    }

    const modeledResult = sumTotalsForModeledVariables(
        rows.map((row) => ({
            variable: row.variable ?? '',
            spend: row.spend,
            contribution: row.contribution,
            contributionProduct: row.contributionProduct,
        })),
    );

    return {
        totals,
        roiTotals: {
            totalContribution: modeledResult.totalContribution,
            totalSpend: modeledResult.totalSpend,
            totalContributionProduct: modeledResult.totalContributionProduct,
        },
    };
};
